import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Alert, Button, Flex, Form, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import { currentLanguage } from '@/app/i18n'
import { requestOtp, type OtpIssued } from '@/features/portal/api'
import { translatePortalError } from '@/shared/api/errors'
import { StepHeader } from '@/shared/ui/StepHeader'

/**
 * E.164: mamlakat kodi bilan jami 8..15 raqam. Chegara MAMLAKATGA
 * BOG'LANMAGAN — `admin`/`partner` panellaridagi `PHONE_E164` bilan bir
 * xil qoida, faqat bu yerda mehmon `+` ni yozmaydi (u maydonning
 * yonida turadi).
 */
const MIN_DIGITS = 8
const MAX_DIGITS = 15

/**
 * Kiritilgan yoki nusxa olingan qiymatdan faqat raqamlarni qoldiradi.
 *
 * ⚠️ Boshidagi nollar OLIB TASHLANADI: mehmon xalqaro raqamni ko'pincha
 * `00998…` shaklida nusxa oladi, E.164 da esa mamlakat kodi hech qachon
 * noldan boshlanmaydi — tozalanmasa `+00998…` serverga ketardi.
 */
const toDigits = (raw: string): string =>
  raw.replace(/\D/g, '').replace(/^0+/, '').slice(0, MAX_DIGITS)

interface SmsPhoneStepProps {
  zoneId: string
  mac: string
  ip: string | null
  onIssued: (challenge: OtpIssued, phoneE164: string) => void
  onBack: () => void
}

/**
 * Telefon raqami. Raqamning o'zi HECH QAYERGA saqlanmaydi (vazifa
 * talabi) — faqat React state ichida, so'rov yuborilgach keyingi
 * qadamga uzatiladi.
 *
 * ⚠️ MAMLAKAT KODI QOTIRILMAGAN. Ilgari bu yerda `+998` turgan va u
 * O'zbekiston №137 qarori bilan asoslangan edi; lekin tizim `devaf`
 * namoyish muhitida Afg'onistonda ham ishlaydi (`+93`), ya'ni qattiq
 * kod o'sha mehmonni BIRINCHI qadamda to'xtatardi va sababini ham
 * noto'g'ri aytardi. Endi mehmon kodni o'zi kiritadi (`sms.phoneHint`),
 * panel esa faqat E.164 uzunligini yumshoq tekshiradi — haqiqiy qarorni
 * SMS shlyuzi qabul qiladi (backend `OtpChallenge.Request` faqat
 * `@NotBlank` talab qiladi).
 */
export function SmsPhoneStep({ zoneId, mac, ip, onIssued, onBack }: SmsPhoneStepProps) {
  const { t } = useTranslation()
  const [digits, setDigits] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (phoneE164: string) =>
      requestOtp({ zoneId, phone: phoneE164, mac, requestIp: ip, locale: currentLanguage() }),
  })

  const phoneE164 = `+${digits}`
  const canSubmit = digits.length >= MIN_DIGITS && digits.length <= MAX_DIGITS

  const handleSubmit = () => {
    setError(null)
    if (!canSubmit) {
      setError(t('sms.phoneInvalid'))
      return
    }
    mutation.mutate(phoneE164, {
      onSuccess: (issued) => onIssued(issued, phoneE164),
      onError: (err) => setError(translatePortalError(err, t).message),
    })
  }

  return (
    <Flex vertical gap={16}>
      <StepHeader title={t('sms.phoneTitle')} subtitle={t('sms.phoneSubtitle')} />

      {error ? <Alert type="error" showIcon message={error} /> : null}

      <Form layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label={t('sms.phoneLabel')}
          extra={t('sms.phoneHint')}
          style={{ marginBottom: 20 }}
        >
          {/* `+` maydonning yonida turadi: mehmon uni yozmaydi, lekin
              raqam mamlakat kodidan boshlanishini ko'radi. */}
          <Input
            addonBefore="+"
            inputMode="numeric"
            autoFocus
            size="large"
            maxLength={MAX_DIGITS}
            value={digits}
            onChange={(event) => setDigits(toDigits(event.target.value))}
          />
        </Form.Item>
        <Flex vertical gap={8}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={mutation.isPending}
            disabled={!canSubmit}
          >
            {mutation.isPending ? t('sms.sending') : t('sms.sendCode')}
          </Button>
          <Button type="text" block onClick={onBack} disabled={mutation.isPending}>
            {t('common.back')}
          </Button>
        </Flex>
      </Form>
    </Flex>
  )
}
